import { trimAllStringValues } from "utils";
import { type CILocation } from "../interfaces";
import type { TFunction } from "i18next";

function cleanUpAddressString(address: string) {
    const separators = new Set([',', '/', '-']);

    const normalizeLine = (line: string) => {
        const normalizedValue = line
            .replaceAll(/\b(?:undefined|null)\b/g, '')
            .replaceAll(/\s+/g, ' ')
            .trim();

        const parts = normalizedValue.split(/([,\/-])/);
        const cleanedParts: string[] = [];

        for (const part of parts) {
            const token = part.trim();

            if (!token) {
                continue;
            }

            if (!separators.has(token)) {
                cleanedParts.push(token);
                continue;
            }

            const hasPreviousValue = cleanedParts.length > 0 && !separators.has(cleanedParts[cleanedParts.length - 1]);
            if (!hasPreviousValue) {
                continue;
            }

            cleanedParts.push(token);
        }

        while (cleanedParts.length > 0 && separators.has(cleanedParts[cleanedParts.length - 1])) {
            cleanedParts.pop();
        }

        return cleanedParts
            .join(' ')
            .replaceAll(/\s*,\s*/g, ', ')
            .replaceAll(/\s*\/\s*/g, ' / ')
            .replaceAll(/\s*-\s*/g, ' - ')
            .replaceAll(/\s+/g, ' ')
            .trim();
    };

    const cleanedLines = address
        .split('\n')
        .map(normalizeLine)
        .filter(Boolean);

    return cleanedLines.join('\n');
}

export function formatAddress(location: CILocation | null, options: { includeCountry?: boolean, cityAndStateOnly?: boolean, multiLineAddress?: boolean, t?: TFunction } = {}) {
    // should this throw an error or silently fail by returning the key passed to it?
    if (!options.t) {
        console.warn('⚠️ Warning: using the `formatAddress()` function without the `t` option may result in some text not being properly translated');
    }
    // if t() is falsy, default t to an identity function f(x) = x
    const { t = ((key: string) => key) as TFunction } = options;
    

    if (!location || Object.values(trimAllStringValues(location)).filter(Boolean).length === 0) {
      return t('location_info_not_available');
    }

    const { includeCountry = false, cityAndStateOnly, multiLineAddress } = options;
    const trimmedLocation = trimAllStringValues(location);

    if (cityAndStateOnly) {
        return cleanUpAddressString(t('address_format_city_state', trimmedLocation) as string) || t('location_info_not_available');
    }

    return cleanUpAddressString(t(`address_format${multiLineAddress ? '_multiline' : ''}`, { ...trimmedLocation, country: includeCountry ? trimmedLocation.country : '' }) as string) || t('location_info_not_available');
}
